import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import SalesDNASettings from '../components/SalesDNASettings';
import { Robot, Sparkle, ChatCircleDots, ArrowRight, Fire } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const getJourneyLabel = (value) => {
  const labels = {
    pronto_atendimento: 'Pronto para Atendimento',
    em_avaliacao: 'Em Avaliação',
    nutricao_futura: 'Nutrição Futura',
    quente: 'Pronto para Atendimento',
    morno: 'Em Avaliação',
    frio: 'Nutrição Futura',
  };
  return labels[value] || value;
};

const getJourneyBadgeClass = (value) => {
  if (value === 'pronto_atendimento' || value === 'quente') return 'bg-emerald-100 text-emerald-700';
  if (value === 'em_avaliacao' || value === 'morno') return 'bg-orange-100 text-orange-700';
  return 'bg-slate-100 text-slate-700';
};


const API = `/api`;

const IAQualificacao = () => {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qualifyLoading, setQualifyLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, {
        withCredentials: true,
      });
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadDetail = async (leadId) => {
    try {
      const { data } = await axios.get(`${API}/leads/${leadId}`, {
        withCredentials: true,
      });
      setSelectedLead(data);
      setConversation(data.conversations || []);
    } catch (error) {
      console.error('Error fetching lead detail:', error);
    }
  };

  const qualifyLead = async () => {
    if (!selectedLead || !message.trim()) return;

    setQualifyLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/concierge/manual-reply`,
        {
          lead_id: selectedLead.id,
          message: message,
        },
        { withCredentials: true }
      );

      // Refresh lead detail
      await fetchLeadDetail(selectedLead.id);
      await fetchLeads();
      setMessage('');
    } catch (error) {
      console.error('Error qualifying lead:', error);
    } finally {
      setQualifyLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="ia-title">
            <Robot size={36} weight="duotone" className="text-purple-600" />
            IA de Qualificação
          </h1>
          <p className="text-slate-600 mt-1">Sistema inteligente de qualificação automática de leads</p>
        </div>

        <SalesDNASettings />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Leads Disponíveis</h3>
            <div className="space-y-2 max-h-[600px] overflow-y-auto scrollbar-thin">
              {leads.filter((l) =>
                l.status === 'novo' ||
                l.status === 'ia_atendendo' ||
                l.conversation_owner === 'ia'
              ).map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => fetchLeadDetail(lead.id)}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedLead?.id === lead.id
                      ? 'bg-purple-50 border-2 border-purple-300'
                      : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                  }`}
                  data-testid={`ia-lead-item-${lead.id}`}
                >
                  <p className="font-medium text-slate-900 text-sm">{lead.name}</p>
                  <p className="text-xs text-slate-600 mt-1">{lead.phone}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-700">
                      {lead.source}
                    </span>
                    {lead.status === 'ia_atendendo' && (
                      <Sparkle size={14} weight="fill" className="text-purple-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conversation */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 flex flex-col h-[700px]">
            {selectedLead ? (
              <>
                <div className="p-5 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900 text-lg">{selectedLead.name}</h3>
                      <p className="text-sm text-slate-600">{selectedLead.enterprise || 'Sem empreendimento'}</p>
                    </div>
                    {selectedLead.score && (
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-3 py-1 rounded-full font-bold ${
                            selectedLead.score >= 70
                              ? 'bg-emerald-100 text-emerald-700'
                              : selectedLead.score >= 40
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          Score: {selectedLead.score}
                        </span>
                        {selectedLead.classification && (
                          <span
                            className={`text-xs px-3 py-1 rounded-full font-medium uppercase ${
                              getJourneyBadgeClass(selectedLead.classification)
                            }`}
                          >
                            {getJourneyLabel(selectedLead.classification)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-1 p-5 overflow-y-auto scrollbar-thin space-y-4">
                  {conversation.length === 0 ? (
                    <div className="text-center py-12">
                      <Robot size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">Nenhuma conversa ainda</p>
                      <p className="text-sm text-slate-400 mt-2">Envie uma mensagem para iniciar a qualificação</p>
                    </div>
                  ) : (
                    conversation.map((conv, index) => (
                      <div
                        key={index}
                        className={`flex ${
                          conv.role === 'ai' ? 'justify-start' : 'justify-end'
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-4 ${
                            conv.role === 'ai'
                              ? 'bg-purple-50 text-slate-900 border border-purple-200'
                              : 'bg-blue-900 text-white'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {conv.role === 'ai' ? (
                              <Robot size={16} weight="fill" className="text-purple-600" />
                            ) : (
                              <ChatCircleDots size={16} weight="fill" />
                            )}
                            <span className="text-xs font-medium">
                              {conv.role === 'ai' ? 'IA' : 'Lead'}
                            </span>
                          </div>
                          <p className="text-sm leading-relaxed">{conv.message}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-5 border-t border-slate-200">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && qualifyLead()}
                      placeholder="Digite a mensagem do lead..."
                      className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      disabled={qualifyLoading}
                      data-testid="ia-message-input"
                    />
                    <Button
                      onClick={qualifyLead}
                      disabled={qualifyLoading || !message.trim()}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-6 rounded-lg flex items-center gap-2"
                      data-testid="ia-qualify-button"
                    >
                      {qualifyLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          Qualificar
                          <ArrowRight size={18} weight="bold" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Robot size={64} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 font-medium">Selecione um lead para iniciar</p>
                  <p className="text-sm text-slate-400 mt-2">A IA está pronta para qualificar</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <Sparkle size={32} weight="duotone" className="mb-3" />
            <h4 className="font-semibold mb-2">Qualificação Automática</h4>
            <p className="text-sm text-purple-100">
              IA identifica intenção de compra, faixa de valor e prazo automaticamente
            </p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
            <Fire size={32} weight="duotone" className="mb-3" />
            <h4 className="font-semibold mb-2">Scoring Inteligente</h4>
            <p className="text-sm text-emerald-100">
              Classificação em Quente, Morno ou Frio com base no perfil do lead
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <ChatCircleDots size={32} weight="duotone" className="mb-3" />
            <h4 className="font-semibold mb-2">Atendimento 24/7</h4>
            <p className="text-sm text-blue-100">
              IA atende leads a qualquer momento, garantindo resposta imediata
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IAQualificacao;